import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { TIER_MONTHLY_PRICES } from '@treu/shared'
import FooterCopyright from '../components/FooterCopyright'

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-12">
    <h2 className="mb-4 inline-block border-b border-paper-200 pb-2 text-lg font-semibold text-ink-900 md:text-xl">
      {title}
    </h2>
    <div className="text-[0.9375rem] leading-relaxed text-ink-600">
      {children}
    </div>
  </section>
)

const TermsPage: React.FC = () => {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  const EFFECTIVE_DATE = 'August 19, 2026'
  const COMPANY_NAME = 'Treu'
  const CONTACT_EMAIL = 'help@treuthat.com'
  const WEBSITE = 'treuthat.com'

  return (
    <div className="min-h-screen bg-paper-0">

      {/* Header */}
      <header className="flex h-16 items-center justify-between border-b border-white/10 bg-brand-950 px-4 md:h-20 md:px-8">
        <Link to="/" className="flex items-center">
          <img src="/assets/logo-white.png" alt="Treu" className="h-[26px] w-auto" />
        </Link>
        <Link
          to="/login"
          className="rounded-sm border border-white/20 px-4 py-2 text-sm text-white/80 transition-colors hover:border-white/40 hover:text-white"
        >
          Sign In
        </Link>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-[720px] px-4 pb-20 pt-12">

        <div className="mb-12">
          <h1 className="mb-2 text-2xl font-bold text-ink-900 md:text-4xl">
            Terms of Service
          </h1>
          <p className="text-sm text-ink-400">
            Effective Date: {EFFECTIVE_DATE}
          </p>
        </div>

        <p className="mb-12 text-[0.9375rem] leading-relaxed text-ink-600">
          Welcome to {COMPANY_NAME}. By accessing or using our website at {WEBSITE}, our mobile applications,
          or our browser extension (collectively, the "Services"), you agree to be bound by these Terms of Service
          ("Terms"). Please read them carefully. If you do not agree to these Terms, do not use the Services.
        </p>

        <Section title="1. Description of Services">
          <p className="mb-4">
            {COMPANY_NAME} is an AI-powered fact-verification platform that helps users assess the accuracy of claims,
            news headlines, and statements by cross-referencing them against authoritative sources, including
            peer-reviewed research, reference works, and major news outlets. Every verification cites its sources
            and links to the original publication.
          </p>
          <p className="mb-4">
            {COMPANY_NAME} is an independent service. It is not affiliated with, endorsed by, or representing any
            government entity, and does not provide government services.
          </p>
          <p className="mb-4">
            Our Services include:
          </p>
          <ul className="mb-4 list-disc space-y-2 pl-6">
            <li>A web dashboard for submitting and reviewing verification results</li>
            <li>Mobile applications (iOS and Android) for on-the-go verification</li>
            <li>A browser extension (Chrome) for in-context verification</li>
            <li>A community platform ("Discerning Minds") for sharing verified facts</li>
          </ul>
          <p>
            <strong>Important:</strong> {COMPANY_NAME} provides information and analysis to assist with evaluating
            claims. Our results are AI-generated and should not be treated as legal, medical, financial, or
            professional advice. Always consult qualified professionals for matters requiring expert guidance.
          </p>
        </Section>

        <Section title="2. Account Registration">
          <p className="mb-4">
            To use the Services, you must create an account by signing in with Google or Apple. By creating an account, you confirm that:
          </p>
          <ul className="mb-4 list-disc space-y-2 pl-6">
            <li>You are at least 13 years of age (or the applicable age of digital consent in your jurisdiction)</li>
            <li>The information you provide is accurate and complete</li>
            <li>You will keep your account credentials secure and confidential</li>
            <li>You are responsible for all activity that occurs under your account</li>
          </ul>
          <p>
            We reserve the right to suspend or terminate accounts that violate these Terms or that we reasonably
            believe are being used fraudulently.
          </p>
        </Section>

        <Section title="3. Subscription Plans and Billing">
          <p className="mb-4">
            {COMPANY_NAME} offers the following subscription tiers:
          </p>
          <ul className="mb-4 list-disc space-y-2 pl-6">
            <li><strong>Free:</strong> 10 verifications per month at no charge</li>
            <li><strong>Basic:</strong> ${TIER_MONTHLY_PRICES.basic}/month — 100 verifications per month</li>
            <li><strong>Premium:</strong> ${TIER_MONTHLY_PRICES.premium}/month — 200 verifications per month, Chrome extension, analytics</li>
            <li><strong>Professional:</strong> ${TIER_MONTHLY_PRICES.professional}/month — Unlimited verifications, advanced AI models, 9+ trusted sources</li>
          </ul>
          <p className="mb-4">
            Payments are processed securely by LemonSqueezy. By subscribing, you authorize LemonSqueezy to charge
            your payment method on a recurring monthly basis. Prices are in USD and do not include applicable taxes.
          </p>
          <p className="mb-4">
            <strong>Cancellation:</strong> You may cancel your subscription at any time through your account settings
            or the LemonSqueezy customer portal. Upon cancellation, your paid plan features remain active until the
            end of your current billing period, after which your account reverts to the Free tier.
          </p>
          <p>
            <strong>Refunds:</strong> We do not generally offer refunds for partial billing periods. If you believe
            you were charged in error, contact us at {CONTACT_EMAIL}.
          </p>
        </Section>

        <Section title="4. Acceptable Use">
          <p className="mb-4">
            You agree to use the Services only for lawful purposes and in accordance with these Terms. You may not:
          </p>
          <ul className="mb-4 list-disc space-y-2 pl-6">
            <li>Use the Services to harass, defame, or harm any individual or group</li>
            <li>Submit content that is illegal, obscene, or infringing on third-party rights</li>
            <li>Attempt to circumvent verification limits or subscription restrictions</li>
            <li>Use automated tools (bots, scrapers) to access the Services without prior written permission</li>
            <li>Reverse engineer, decompile, or attempt to extract source code from the Services</li>
            <li>Use the Services in any way that could damage, disable, or impair our infrastructure</li>
          </ul>
          <p>
            Violations may result in immediate account suspension or termination without notice or refund.
          </p>
        </Section>

        <Section title="5. Use of AI Technology">
          <p className="mb-4">
            Our verification engine uses large language models (including Google Gemini and Anthropic Claude) to
            analyse claims. By using the Services, you acknowledge that:
          </p>
          <ul className="mb-4 list-disc space-y-2 pl-6">
            <li>AI-generated results may contain errors, omissions, or inaccuracies</li>
            <li>Verification results represent the AI's assessment at the time of processing based on available sources</li>
            <li>No AI system can guarantee 100% accuracy, and independent verification of important claims is always advisable</li>
            <li>Your submitted queries may be processed by third-party AI providers subject to their own terms and policies</li>
          </ul>
        </Section>

        <Section title="6. Intellectual Property">
          <p className="mb-4">
            All content, design, code, and trademarks associated with {COMPANY_NAME} are the property of {COMPANY_NAME}
            and are protected by applicable intellectual property laws. You may not reproduce, distribute, or create
            derivative works without our express written consent.
          </p>
          <p>
            You retain ownership of any content you submit to the Services. By submitting content, you grant
            {COMPANY_NAME} a non-exclusive, royalty-free licence to use that content solely for the purpose of
            providing and improving the Services.
          </p>
        </Section>

        <Section title="7. Privacy">
          <p>
            Your privacy is important to us. Our collection and use of personal information is governed by our{' '}
            <Link to="/privacy" className="text-accent-600 underline transition-colors hover:text-accent-500">Privacy Policy</Link>,
            which is incorporated into these Terms by reference. Please review it carefully.
          </p>
        </Section>

        <Section title="8. Disclaimer of Warranties">
          <p className="mb-4">
            THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED,
            INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
            NON-INFRINGEMENT.
          </p>
          <p>
            {COMPANY_NAME} does not warrant that the Services will be uninterrupted, error-free, or that
            verification results will be accurate, complete, or suitable for any particular purpose.
          </p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p className="mb-4">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY_NAME.toUpperCase()} AND ITS OFFICERS, DIRECTORS,
            EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
            PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICES.
          </p>
          <p>
            Our total liability to you for any claim arising from these Terms or the Services shall not exceed the
            greater of (a) the amount you paid to {COMPANY_NAME} in the three months preceding the claim, or (b) $10 USD.
          </p>
        </Section>

        <Section title="10. Termination">
          <p className="mb-4">
            You may delete your account at any time through your account settings. Upon deletion, your data will be
            permanently removed in accordance with our Privacy Policy.
          </p>
          <p>
            We may suspend or terminate your access to the Services at any time for violation of these Terms, or
            for any other reason at our sole discretion, with or without notice. Upon termination, your right to
            use the Services ceases immediately.
          </p>
        </Section>

        <Section title="11. Governing Law">
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the State of Delaware,
            United States, without regard to its conflict of law provisions. Any disputes arising under these Terms
            shall be subject to the exclusive jurisdiction of the courts located in Delaware.
          </p>
        </Section>

        <Section title="12. Changes to These Terms">
          <p>
            We may update these Terms from time to time. When we do, we will revise the Effective Date above and,
            for material changes, provide notice via email or through the Services. Your continued use of the
            Services after any changes constitutes acceptance of the revised Terms.
          </p>
        </Section>

        <Section title="13. Contact Us">
          <p>
            If you have any questions about these Terms, please contact us at:{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent-600 underline transition-colors hover:text-accent-500">{CONTACT_EMAIL}</a>
          </p>
        </Section>

      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-brand-950 px-4 py-8 text-center">
        <div className="mb-4 flex flex-wrap justify-center gap-8">
          <Link to="/" className="text-sm text-white/50 transition-colors hover:text-white/80">Home</Link>
          <Link to="/privacy" className="text-sm text-white/50 transition-colors hover:text-white/80">Privacy Policy</Link>
          <Link to="/contact" className="text-sm text-white/50 transition-colors hover:text-white/80">Contact</Link>
        </div>
        <FooterCopyright variant="full" className="text-white/50" />
      </footer>

    </div>
  )
}

export default TermsPage
